import { ApiClientError } from '@/lib/api-client';
import type { LocationImage, RemoveImageResponse } from '@/types/api';

/** The success toast's text: says where the removal took effect, by `publishedUpdate`. */
export function describeImageRemoval(
  result: Pick<RemoveImageResponse, 'publishedUpdate' | 'remainingImageCount'>
): string {
  const remaining = `${String(result.remainingImageCount)} image(s) remain.`;
  switch (result.publishedUpdate) {
    case 'none':
      return `${remaining} The removal survives a re-upload of the import file.`;
    case 'deleted':
      return `Removed from ScoutAround as well. ${remaining}`;
    case 'deleted_and_cover_reset':
      return `Removed from ScoutAround as well; the first remaining image is now the cover. ${remaining}`;
    case 'mapping_missing':
      // The Bridge holds no mapping for it; the photo may still be live on
      // ScoutAround from an old upload whose mapping was never recorded.
      return `The Bridge has no record of this image on ScoutAround, so nothing was deleted there. ${remaining}`;
    default:
      // A value a newer server added: say only what is certain.
      return `The image was removed from this listing. ${remaining}`;
  }
}

/** The status line `api-client.ts` uses when the error body had no `message`. */
const GENERIC_API_ERROR_PREFIX = 'API error:';

/**
 * The route's error texts after which a retry is known to be safe: ScoutAround
 * refused or could not be reached (nothing changed in the Bridge), or
 * ScoutAround deleted the image and the Bridge write failed (the backend
 * answers 200 for an image already gone, so the retry records the removal).
 * Mirrors SCOUTAROUND_DELETE_FAILED_PREFIX and IMAGE_REMOVAL_ERRORS.bridgeWriteFailed
 * in bridge/backend/src/modules/synced-locations/image-removal.service.ts.
 */
const SCOUTAROUND_DELETE_FAILED_PREFIX = 'Failed to delete the image on ScoutAround: ';
const BRIDGE_WRITE_FAILED = 'The image was removed on ScoutAround but the Bridge could not record it. Retry the removal.';
const RETRY_IS_SAFE = 'Retrying is safe.';

/**
 * The failure toast's text. The removal route answers `{ success: false, error }`
 * with no `message`, so the API client keeps the server's sentence in
 * `errorCode` and puts its generic status line in `message`; that sentence is
 * shown then. A Fastify error (`{ error: 'Unauthorized', message: '…' }`) has
 * a real `message` and a short name in `errorCode`, so the message wins there.
 * The server's text comes first; for the retryable failures above it is
 * followed by a note that retrying is safe.
 */
export function imageRemovalErrorText(error: unknown): string {
  const text = serverErrorText(error);
  if (text.startsWith(SCOUTAROUND_DELETE_FAILED_PREFIX) || text === BRIDGE_WRITE_FAILED) {
    return `${text}${/[.!?]$/.test(text) ? '' : '.'} ${RETRY_IS_SAFE}`;
  }
  return text;
}

function serverErrorText(error: unknown): string {
  if (error instanceof ApiClientError) {
    if (error.message.startsWith(GENERIC_API_ERROR_PREFIX)) {
      return error.errorCode ?? error.message;
    }
    return error.message || (error.errorCode ?? 'Unknown error');
  }
  return error instanceof Error ? error.message : 'Unknown error';
}

/** The gallery in the order the grid shows it (by `order`), as a new array. */
export function sortGalleryByOrder<T extends Pick<LocationImage, 'order'>>(images: readonly T[]): T[] {
  return [...images].sort((a, b) => a.order - b.order);
}

/** One photo's outcome in a multi-photo removal: the server's result, or the error it failed with. */
export type BulkImageRemovalOutcome =
  | { ok: true; result: Pick<RemoveImageResponse, 'publishedUpdate' | 'remainingImageCount'> }
  | { ok: false; error: unknown };

/**
 * The toast for a multi-photo removal. The photos are removed one request at a
 * time, so each succeeded or failed on its own: the title counts both, and the
 * description says where the removals took effect (from the last success's
 * remaining count) and, on a failure, the first failure's text.
 */
export function describeBulkImageRemoval(outcomes: readonly BulkImageRemovalOutcome[]): {
  title: string;
  description: string;
  failed: boolean;
} {
  const total = outcomes.length;
  const successes = outcomes.flatMap((outcome) => (outcome.ok ? [outcome.result] : []));
  const firstFailure = outcomes.find((outcome): outcome is { ok: false; error: unknown } => !outcome.ok);
  const lastSuccess = successes[successes.length - 1];

  const notes: string[] = [];
  if (successes.some((result) => result.publishedUpdate === 'deleted' || result.publishedUpdate === 'deleted_and_cover_reset')) {
    notes.push('Removed from ScoutAround as well.');
  }
  if (successes.some((result) => result.publishedUpdate === 'deleted_and_cover_reset')) {
    notes.push('The first remaining image is now the cover.');
  }
  if (successes.some((result) => result.publishedUpdate === 'mapping_missing')) {
    notes.push('For some images the Bridge had no record on ScoutAround, so nothing was deleted there.');
  }
  if (lastSuccess) {
    notes.push(`${String(lastSuccess.remainingImageCount)} image(s) remain.`);
  }

  if (!firstFailure) {
    return { title: `${String(total)} images removed`, description: notes.join(' '), failed: false };
  }
  return {
    title: `Removed ${String(successes.length)} of ${String(total)} images`,
    description: [`${String(total - successes.length)} not removed: ${imageRemovalErrorText(firstFailure.error)}`, ...notes].join(' '),
    failed: true,
  };
}
